import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { i18n } from '../../translate/i18n';
import useKanbanAutomations from '../../hooks/useKanbanAutomations';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  ruleCard: {
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  triggerCard: {
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.primary.light + '10',
  },
  actionCard: {
    borderColor: theme.palette.secondary.light,
    backgroundColor: theme.palette.secondary.light + '10',
  },
  previewBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  formControl: {
    minWidth: 200,
    marginBottom: theme.spacing(2),
  },
}));

const KanbanRuleBuilder = ({ open, onClose, automation, onSave }) => {
  const classes = useStyles();
  const { createAutomation, updateAutomation } = useKanbanAutomations();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    active: true,
    triggerType: 'ticket_moved',
    triggerConfig: {},
    actions: [{
      type: 'assign_user',
      parameters: {}
    }],
    conditions: []
  });

  useEffect(() => {
    if (automation) {
      // Converter estrutura do backend para o formulário
      const firstAction = automation.actions && automation.actions.length > 0
        ? automation.actions[0]
        : { type: 'assign_user', parameters: {} };

      setFormData({
        name: automation.name || '',
        description: automation.description || '',
        active: automation.active !== undefined ? automation.active : true,
        triggerType: automation.triggerType || 'ticket_moved',
        triggerConfig: automation.triggerConfig || {},
        actions: automation.actions || [{ type: 'assign_user', parameters: {} }],
        conditions: automation.conditions || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        active: true,
        triggerType: 'ticket_moved',
        triggerConfig: {},
        actions: [{
          type: 'assign_user',
          parameters: {}
        }],
        conditions: []
      });
    }
  }, [automation, open]);

  const triggerTypes = [
    { value: 'ticket_moved', label: i18n.t('kanban.automation.triggerTypes.ticket_moved') },
    { value: 'task_completed', label: i18n.t('kanban.automation.triggerTypes.task_completed') },
    { value: 'time_based', label: i18n.t('kanban.automation.triggerTypes.time_based') },
    { value: 'field_changed', label: i18n.t('kanban.automation.triggerTypes.field_changed') },
    { value: 'tag_assigned', label: i18n.t('kanban.automation.triggerTypes.tag_assigned') },
    { value: 'user_assigned', label: i18n.t('kanban.automation.triggerTypes.user_assigned') }
  ];

  const actionTypes = [
    { value: 'move_ticket', label: i18n.t('kanban.automation.actionTypes.move_ticket') },
    { value: 'assign_user', label: i18n.t('kanban.automation.actionTypes.assign_user') },
    { value: 'send_notification', label: i18n.t('kanban.automation.actionTypes.send_notification') },
    { value: 'update_field', label: i18n.t('kanban.automation.actionTypes.update_field') },
    { value: 'create_task', label: i18n.t('kanban.automation.actionTypes.create_task') },
    { value: 'change_priority', label: i18n.t('kanban.automation.actionTypes.change_priority') },
    { value: 'set_due_date', label: i18n.t('kanban.automation.actionTypes.set_due_date') }
  ];

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTriggerChange = (value) => {
    setFormData(prev => ({
      ...prev,
      triggerType: value,
      triggerConfig: {} // Reset config when changing trigger type
    }));
  };

  const handleTriggerConfigChange = (configKey, value) => {
    setFormData(prev => ({
      ...prev,
      triggerConfig: {
        ...prev.triggerConfig,
        [configKey]: value
      }
    }));
  };

  const handleActionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      actions: [{
        type: value,
        parameters: {} // Reset parameters when changing action type
      }]
    }));
  };

  const handleActionConfigChange = (paramKey, value) => {
    setFormData(prev => ({
      ...prev,
      actions: [{
        ...prev.actions[0],
        parameters: {
          ...prev.actions[0].parameters,
          [paramKey]: value
        }
      }]
    }));
  };

  const handleSave = async () => {
    try {
      if (automation) {
        await updateAutomation(automation.id, formData);
      } else {
        await createAutomation(formData);
      }
      onSave();
    } catch (error) {
      console.error('Error saving automation:', error);
    }
  };

  const renderTriggerConfig = () => {
    switch (formData.triggerType) {
      case 'ticket_moved':
        return (
          <Box>
            <TextField
              label={i18n.t('kanban.automation.fromTag')}
              value={formData.triggerConfig.fromTagId || ''}
              onChange={(e) => handleTriggerConfigChange('fromTagId', e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              helperText="ID da tag de origem (deixe vazio para qualquer)"
            />
            <TextField
              label={i18n.t('kanban.automation.toTag')}
              value={formData.triggerConfig.toTagId || ''}
              onChange={(e) => handleTriggerConfigChange('toTagId', e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              helperText="ID da tag de destino (deixe vazio para qualquer)"
            />
          </Box>
        );
      case 'time_based':
        return (
          <Box>
            <TextField
              label={i18n.t('kanban.automation.schedule')}
              value={formData.triggerConfig.schedule || ''}
              onChange={(e) => handleTriggerConfigChange('schedule', e.target.value)}
              fullWidth
              margin="normal"
              placeholder="0 9 * * 1-5" // Every weekday at 9 AM
              helperText={i18n.t('kanban.automation.cronHelp')}
            />
          </Box>
        );
      case 'field_changed':
        return (
          <Box>
            <TextField
              label={i18n.t('kanban.automation.fieldName')}
              value={formData.triggerConfig.fieldName || ''}
              onChange={(e) => handleTriggerConfigChange('fieldName', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Nome do campo (ex: status, priority, assigneeId)"
            />
            <TextField
              label={i18n.t('kanban.automation.fromValue')}
              value={formData.triggerConfig.fromValue || ''}
              onChange={(e) => handleTriggerConfigChange('fromValue', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Valor anterior (deixe vazio para qualquer)"
            />
            <TextField
              label={i18n.t('kanban.automation.toValue')}
              value={formData.triggerConfig.toValue || ''}
              onChange={(e) => handleTriggerConfigChange('toValue', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Novo valor (deixe vazio para qualquer)"
            />
          </Box>
        );
      case 'tag_assigned':
        return (
          <Box>
            <TextField
              label="ID da Tag"
              value={formData.triggerConfig.tagId || ''}
              onChange={(e) => handleTriggerConfigChange('tagId', e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              helperText="ID da tag que deve ser atribuída (deixe vazio para qualquer tag)"
            />
          </Box>
        );
      case 'user_assigned':
        return (
          <Box>
            <TextField
              label="ID do Usuário"
              value={formData.triggerConfig.userId || ''}
              onChange={(e) => handleTriggerConfigChange('userId', e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              helperText="ID do usuário atribuído (deixe vazio para qualquer usuário)"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const renderActionConfig = () => {
    const currentAction = formData.actions[0];
    switch (currentAction.type) {
      case 'move_ticket':
        return (
          <TextField
            label={i18n.t('kanban.automation.targetTag')}
            value={currentAction.parameters.targetTagId || ''}
            onChange={(e) => handleActionConfigChange('targetTagId', e.target.value)}
            fullWidth
            margin="normal"
            type="number"
            helperText="ID da tag para onde o ticket será movido"
            required
          />
        );
      case 'assign_user':
        return (
          <Box>
            <TextField
              label={i18n.t('kanban.automation.userId')}
              value={currentAction.parameters.userId || ''}
              onChange={(e) => handleActionConfigChange('userId', e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              helperText="ID do usuário a ser atribuído"
              required
            />
            <TextField
              label="Função (role)"
              value={currentAction.parameters.role || 'assignee'}
              onChange={(e) => handleActionConfigChange('role', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Função do usuário (assignee, watcher, reviewer)"
            />
          </Box>
        );
      case 'send_notification':
        return (
          <Box>
            <TextField
              label={i18n.t('kanban.automation.notificationMessage')}
              value={currentAction.parameters.message || ''}
              onChange={(e) => handleActionConfigChange('message', e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              helperText="Mensagem da notificação"
              required
            />
            <TextField
              label="IDs dos Destinatários (separados por vírgula)"
              value={currentAction.parameters.recipients?.join(', ') || ''}
              onChange={(e) => {
                const recipients = e.target.value
                  .split(',')
                  .map(id => id.trim())
                  .filter(id => id)
                  .map(id => parseInt(id));
                handleActionConfigChange('recipients', recipients);
              }}
              fullWidth
              margin="normal"
              helperText="Deixe vazio para notificar todos na empresa"
            />
            <FormControl className={classes.formControl}>
              <InputLabel>Tipo da Notificação</InputLabel>
              <Select
                value={currentAction.parameters.type || 'info'}
                onChange={(e) => handleActionConfigChange('type', e.target.value)}
              >
                <MenuItem value="info">Informação</MenuItem>
                <MenuItem value="warning">Aviso</MenuItem>
                <MenuItem value="error">Erro</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      case 'update_field':
        return (
          <Box>
            <TextField
              label="Nome do Campo"
              value={currentAction.parameters.field || ''}
              onChange={(e) => handleActionConfigChange('field', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Nome do campo a ser atualizado"
              required
            />
            <TextField
              label="Novo Valor"
              value={currentAction.parameters.value || ''}
              onChange={(e) => handleActionConfigChange('value', e.target.value)}
              fullWidth
              margin="normal"
              helperText="Novo valor para o campo"
              required
            />
            <FormControl className={classes.formControl}>
              <InputLabel>Alvo</InputLabel>
              <Select
                value={currentAction.parameters.target || 'ticket'}
                onChange={(e) => handleActionConfigChange('target', e.target.value)}
              >
                <MenuItem value="ticket">Ticket</MenuItem>
                <MenuItem value="task">Tarefa</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      case 'create_task':
        return (
          <Box>
            <TextField
              label="Título da Tarefa"
              value={currentAction.parameters.title || ''}
              onChange={(e) => handleActionConfigChange('title', e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Descrição"
              value={currentAction.parameters.description || ''}
              onChange={(e) => handleActionConfigChange('description', e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={2}
            />
            <FormControl className={classes.formControl}>
              <InputLabel>Prioridade</InputLabel>
              <Select
                value={currentAction.parameters.priority || 'medium'}
                onChange={(e) => handleActionConfigChange('priority', e.target.value)}
              >
                <MenuItem value="low">Baixa</MenuItem>
                <MenuItem value="medium">Média</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="ID do Responsável"
              value={currentAction.parameters.assigneeId || ''}
              onChange={(e) => handleActionConfigChange('assigneeId', e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              helperText="Deixe vazio para não atribuir"
            />
          </Box>
        );
      case 'change_priority':
        return (
          <Box>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>{i18n.t('kanban.automation.newPriority')}</InputLabel>
              <Select
                value={currentAction.parameters.priority || 'medium'}
                onChange={(e) => handleActionConfigChange('priority', e.target.value)}
              >
                <MenuItem value="low">Baixa</MenuItem>
                <MenuItem value="medium">Média</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>Alvo</InputLabel>
              <Select
                value={currentAction.parameters.target || 'ticket'}
                onChange={(e) => handleActionConfigChange('target', e.target.value)}
              >
                <MenuItem value="ticket">Ticket</MenuItem>
                <MenuItem value="task">Tarefa</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      case 'set_due_date':
        return (
          <Box>
            <TextField
              label="Data de Vencimento (ISO String ou dias a partir de hoje)"
              value={currentAction.parameters.dueDate || ''}
              onChange={(e) => {
                const value = e.target.value;
                // Se for um número, calcular a data
                if (!isNaN(value) && value.trim() !== '') {
                  const days = parseInt(value);
                  const futureDate = new Date();
                  futureDate.setDate(futureDate.getDate() + days);
                  handleActionConfigChange('dueDate', futureDate.toISOString());
                } else {
                  handleActionConfigChange('dueDate', value);
                }
              }}
              fullWidth
              margin="normal"
              helperText="Ex: 7 (para 7 dias) ou data ISO (2024-12-31T00:00:00.000Z)"
              required
            />
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>Alvo</InputLabel>
              <Select
                value={currentAction.parameters.target || 'ticket'}
                onChange={(e) => handleActionConfigChange('target', e.target.value)}
              >
                <MenuItem value="ticket">Ticket</MenuItem>
                <MenuItem value="task">Tarefa</MenuItem>
              </Select>
            </FormControl>
          </Box>
        );
      default:
        return null;
    }
  };

  const generateRulePreview = () => {
    const triggerLabel = triggerTypes.find(t => t.value === formData.triggerType)?.label || formData.triggerType;
    const actionLabel = actionTypes.find(a => a.value === formData.actions[0].type)?.label || formData.actions[0].type;

    return `${i18n.t('kanban.automation.when')} ${triggerLabel} → ${i18n.t('kanban.automation.then')} ${actionLabel}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {automation ? i18n.t('kanban.automation.editRule') : i18n.t('kanban.automation.createRule')}
      </DialogTitle>
      <DialogContent>
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.automation.basicInfo')}
          </Typography>
          <TextField
            label={i18n.t('kanban.automation.ruleName')}
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label={i18n.t('kanban.automation.description')}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => handleFieldChange('active', e.target.checked)}
                color="primary"
              />
            }
            label={i18n.t('kanban.automation.activeRule')}
          />
        </Box>

        <Divider />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.automation.triggers')}
          </Typography>
          <Card className={`${classes.ruleCard} ${classes.triggerCard}`}>
            <CardContent>
              <FormControl className={classes.formControl}>
                <InputLabel>{i18n.t('kanban.automation.triggerType')}</InputLabel>
                <Select
                  value={formData.triggerType}
                  onChange={(e) => handleTriggerChange(e.target.value)}
                >
                  {triggerTypes.map(trigger => (
                    <MenuItem key={trigger.value} value={trigger.value}>
                      {trigger.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {renderTriggerConfig()}
            </CardContent>
          </Card>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.automation.actions')}
          </Typography>
          <Card className={`${classes.ruleCard} ${classes.actionCard}`}>
            <CardContent>
              <FormControl className={classes.formControl}>
                <InputLabel>{i18n.t('kanban.automation.actionType')}</InputLabel>
                <Select
                  value={formData.actions[0].type}
                  onChange={(e) => handleActionChange(e.target.value)}
                >
                  {actionTypes.map(action => (
                    <MenuItem key={action.value} value={action.value}>
                      {action.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {renderActionConfig()}
            </CardContent>
          </Card>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>
            {i18n.t('kanban.automation.preview')}
          </Typography>
          <Box className={classes.previewBox}>
            {generateRulePreview()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {i18n.t('kanban.cancel')}
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={!formData.name.trim()}
        >
          {automation ? i18n.t('kanban.update') : i18n.t('kanban.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KanbanRuleBuilder;